import { ActionPanel } from "../actionPanel/ActionPanel";
import {
  Button,
  Dialog,
  Icon,
  DialogNavigation,
  DialogContent,
} from "@sellar-io/components";
import { useState } from "react";
import { useOrder } from "../../hooks/api/v2/order/useOrder";
import { SpinnerLegacy } from "..";
import { SellarCreditOrderCreateInvoiceDialog } from "../../pages/order/modals/SellarCreditOrderCreateInvoiceDialog";
import { useCreditProcessorDownloadOrderInvoice } from "../../hooks/api/v2/creditProcessor/useCreditProcessorDownloadOrderInvoice";
import { downloadPDF } from "../../utils/downloadHelpers";
import { useToast } from "../toast/useToast.shared";
import { Toast } from "../toast/Toast";

export const SellarCreditOrderVatInvoiceActionPanel = ({
  orderId,
}: {
  orderId: number;
}) => {
  const { data: order, isPending: isFetchingOrder } = useOrder(orderId, {
    enabled: Boolean(orderId),
  });

  const isTwoOrder = Boolean(order?.TwoOrder?.id);

  const renderAccessVatInvoice = () => {
    if (isTwoOrder) {
      return (
        <TwoAccessVatInvoiceButton
          isFetchingOrder={isFetchingOrder || !order}
          orderId={orderId}
        />
      );
    }

    return (
      <HokodoAccessVatInvoiceButton
        isFetchingOrder={isFetchingOrder || !order}
        orderId={orderId}
      />
    );
  };

  return (
    <>
      <ActionPanel
        className="flex flex-col gap-4"
        title="Your VAT invoice is available"
      >
        <div className="flex flex-col gap-2 md:flex-row">
          {!order ? <SpinnerLegacy /> : renderAccessVatInvoice()}
        </div>
      </ActionPanel>
    </>
  );
};

const HokodoAccessVatInvoiceButton = ({
  orderId,
  isFetchingOrder,
}: {
  orderId: number;
  isFetchingOrder: boolean;
}) => {
  const [invoiceCreateDialogActive, setInvoiceCreateDialogActive] =
    useState(false);
  return (
    <>
      <Button
        loading={isFetchingOrder}
        className={"w-full md:w-min"}
        onClick={() => setInvoiceCreateDialogActive(true)}
      >
        <Icon icon="FILE_DOCUMENT_ARROW_RIGHT_OUTLINE" />
        Access Vat Invoice
      </Button>
      <SellarCreditOrderCreateInvoiceDialog
        open={invoiceCreateDialogActive}
        onDismiss={() => setInvoiceCreateDialogActive(false)}
        orderId={orderId}
      />
    </>
  );
};

const TwoAccessVatInvoiceButton = ({
  isFetchingOrder,
  orderId,
}: {
  isFetchingOrder: boolean;
  orderId: number;
}) => {
  const [openDialog, setOpenDialog] = useState(false);
  const { toast } = useToast();

  const creditProcessorDownloadInvoiceMutation =
    useCreditProcessorDownloadOrderInvoice();

  const handleDownloadInvoice = ({ orderId }: { orderId: number }) => {
    setOpenDialog(true);

    creditProcessorDownloadInvoiceMutation.mutate(
      {
        orderId,
      },
      {
        onSuccess: (data) => {
          const documentName = "Invoice";
          const documentFileName = `${documentName} - ${orderId}.pdf`;
          downloadPDF(documentFileName, data.document);
          setOpenDialog(false);
        },
        onError: () => {
          toast({
            placement: "bottom",
            render: () => (
              <Toast icon="ALERT_FILLED">
                Unable to download invoice at this time
              </Toast>
            ),
          });
          setOpenDialog(false);
        },
      },
    );
  };

  return (
    <>
      <Button
        loading={isFetchingOrder}
        className={"w-full md:w-min"}
        onClick={() => handleDownloadInvoice({ orderId })}
      >
        <Icon icon="FILE_DOCUMENT_ARROW_RIGHT_OUTLINE" />
        Access Vat Invoice
      </Button>
      <DownloadDialog
        active={openDialog}
        onDismiss={() => setOpenDialog(false)}
      />
    </>
  );
};

type DownloadDialogProps = {
  active: boolean;
  onDismiss: () => void;
};
export const DownloadDialog = ({ onDismiss, active }: DownloadDialogProps) => {
  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        leftIcon="CHEVRON_DOWN"
        onClickLeftIcon={onDismiss}
        title="Downloading VAT Invoice"
      />
      <DialogContent>
        <SpinnerLegacy />
        <span>
          Download can take up to 10 seconds. Please do not navigate away until
          your download is complete.
        </span>
      </DialogContent>
    </Dialog>
  );
};
